// Presentation of the caller call: ringing / queue (screen 3-4) and in-call (screen 5).
// IMPORTANT: the media elements stay ALWAYS MOUNTED (visibility via CSS). Remote tracks
// arrive on the refs/ids (remoteVideo/remoteAudio) and must find a live element.
import { useContext, useEffect, useRef, useState } from 'react'
import BrandLockup from '../components/BrandLockup'
import { useNavigate } from 'react-router-dom'
import VideoContext from '../core/VideoContext'
import { convertTimerFormat } from '../core/callStatus'
import CallControls from '../components/CallControls'
import { MicOffIcon, PhoneIcon, PhoneOffIcon, StatusIcon } from '../components/icons'
import { t } from '../i18n'

const CONTROLS_AUTOHIDE_MS = 4000

const initialsOf = (name: string) => {
  if (!name) return '··'
  const clean = name.includes('@') ? name.split('@')[0] : name
  const parts = clean.split(/[\s._-]+/).filter(Boolean)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || clean.slice(0, 2).toUpperCase()
}

const CallView = () => {
  const navigate = useNavigate()
  const {
    staffTimeOut,
    callRejected,
    sec,
    callAccepted,
    myVideo,
    userVideo,
    userAudio,
    remoteUsername,
    isMyVideoOn,
    userVdoStatus,
    myMicStatus,
    userMicStatus,
    updateMic,
    remoteUserSocketId,
    staffUnavailable,
    message,
    callStatus,
    queuePosition,
    webrtcState,
    leaveCall,
    callType,
    toggleVideoCall,
    switchCam,
    supportsMultipleCameras,
    isCallConnected,
    isHideOptionButtons,
    isDirectCallEnabled,
  } = useContext(VideoContext)

  const [controlsVisible, setControlsVisible] = useState(true)
  const hideTimer = useRef<any>(null)

  const inCall = Boolean(callAccepted || remoteUserSocketId)
  const failed = Boolean(staffUnavailable || callRejected || staffTimeOut)
  const degraded = callAccepted && (webrtcState === 'disconnected' || webrtcState === 'failed')
  // Show the remote video whenever the OTHER party is sending video (userVdoStatus), not only
  // when this side is a video call. Otherwise, on a call that started in audio mode, the remote
  // (e.g. the agent) turning their camera on delivered the track but it stayed hidden.
  const showRemoteVideo = callType === 'video' || userVdoStatus

  // AUTO-HIDE THE CONTROL BAR; ANY TAP ON THE STAGE BRINGS IT BACK.
  const pokeControls = () => {
    setControlsVisible(true)
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setControlsVisible(false), CONTROLS_AUTOHIDE_MS)
  }

  useEffect(() => {
    if (isCallConnected) pokeControls()
    return () => clearTimeout(hideTimer.current)
  }, [isCallConnected])

  const backToStart = () => {
    const scanUrl = sessionStorage.getItem('cf_scan_url')
    if (scanUrl) {
      window.location.hash = scanUrl.startsWith('#') ? scanUrl.slice(1) : scanUrl
    } else {
      navigate('/invalid-qr')
    }
  }

  const showControls = !(isHideOptionButtons && !isCallConnected)

  return (
    <>
      {/* FAILURE PANEL (NO STAFF / REJECTED / TIMED OUT) */}
      {failed && (
        <div className="cf-page">
          <div className="cf-card">
            <BrandLockup />
            <StatusIcon tone="warning" icon={PhoneOffIcon} />
            <h1 className="cf-title">{t.cannotAssistTitle}</h1>
            <p className="cf-subtitle">{message || t.allBusy}</p>
            <button className="cf-btn cf-btn-primary" onClick={backToStart}>
              {t.retry}
            </button>
          </div>
        </div>
      )}

      {/* SCREENS 3-4: CALLING / QUEUE (HIDDEN ONCE THE CALL IS UP) */}
      <div className="cf-calling" style={{ display: failed || inCall ? 'none' : 'flex' }}>
        <div className="cf-pulse">
          <div className="cf-pulse-core">
            <PhoneIcon />
          </div>
        </div>
        <div className="cf-status-text">{callStatus || t.connectingCall}</div>
        {queuePosition && (
          <div key={queuePosition} className="cf-queue-badge">
            {t.queuePosition(queuePosition)}
          </div>
        )}
        <button className="cf-btn cf-btn-danger cf-cancel" onClick={() => leaveCall()}>
          {t.cancel}
        </button>
      </div>

      {/* SCREEN 5: IN CALL (ELEMENTS ALWAYS MOUNTED) */}
      <div className="cf-call-stage" style={{ display: failed || !inCall ? 'none' : 'block' }} onClick={pokeControls}>
        <video
          id="remoteVideo"
          ref={userVideo}
          autoPlay
          playsInline
          className="cf-remote-video"
          style={{ visibility: showRemoteVideo ? 'visible' : 'hidden' }}
        />
        <audio id="remoteAudio" ref={userAudio} autoPlay />

        {!showRemoteVideo && (
          <div className="cf-remote-placeholder">
            <div className="cf-avatar">{initialsOf(remoteUsername)}</div>
            <div className="cf-remote-name">{t.beingAssisted}</div>
            {callType === 'video' && <div className="cf-remote-hint">{t.cameraOff}</div>}
          </div>
        )}

        {isCallConnected && <div className="cf-timer">{convertTimerFormat(sec)}</div>}
        {degraded && <div className="cf-degraded">{t.unstableConnection}</div>}
        {userMicStatus === false && (
          <div className="cf-remote-muted">
            <MicOffIcon />
            {t.micOff}
          </div>
        )}

        <video
          ref={myVideo}
          autoPlay
          muted
          playsInline
          className="cf-local-preview"
          // KIOSK (direct-call) HAS NO USEFUL SELF-VIEW (the camera points at the desk) and the
          // local stream is never attached to this element in direct-call mode, so the box would
          // show empty. Restore the old app's gate: only show the self-view for non-direct callers.
          style={{ display: !isDirectCallEnabled?.current && isMyVideoOn && callType === 'video' ? 'block' : 'none' }}
        />

        {showControls && (
          <CallControls
            micOn={Boolean(myMicStatus)}
            videoOn={callType === 'video'}
            canSwitchCam={Boolean(supportsMultipleCameras && callType === 'video')}
            visible={controlsVisible}
            onToggleMic={updateMic}
            onToggleVideo={toggleVideoCall}
            onSwitchCam={switchCam}
            onHangup={() => leaveCall()}
            poke={pokeControls}
          />
        )}
      </div>
    </>
  )
}

export default CallView
